import type { PublicProduct } from '../graphql/public'
import { groupDrinks } from '../lib/catalog'
import { ColumnTitle } from './ColumnTitle'
import { LineRow } from './LineRow'
import { Deposit, Price } from './Price'

// Eight of nine drinks share one price and one deposit, so they are one decision:
// their names go on a single line under a single price. That is what makes the
// column fit without rotating it away (lessons.md). Groups below three stay rows.
export function DrinksColumn({
  title,
  products,
  isFrom = () => false,
}: {
  title: string
  products: PublicProduct[]
  /** Whether a variant's list price is a *from* price (useLiveCatalog). */
  isFrom?: (variantId: number) => boolean
}) {
  const lines = groupDrinks(products, isFrom)
  if (lines.length === 0) return null
  return (
    <div className="flex flex-col">
      <ColumnTitle>{title}</ColumnTitle>
      {lines.map((line) =>
        line.kind === 'group' ? (
          <div
            key={'g' + line.price_cents + '-' + line.deposit_cents + (line.from ? '-from' : '')}
            className="flex items-start justify-between space-x-[24px] py-[10px] border-b-2 border-brand-line"
          >
            <div className="font-primary font-bold text-[28px] leading-[1.35] text-secondary-800">
              {line.products.map((p, i) => (
                // A name never breaks across lines; the separator trails it so a line
                // ends on a dot and the next starts on a name.
                <span key={p.id}>
                  <span className="whitespace-nowrap">
                    {p.name}
                    {i < line.products.length - 1 ? (
                      <span className="font-secondary font-normal text-brand-butterscotch">{' ·'}</span>
                    ) : null}
                  </span>
                  {i < line.products.length - 1 ? ' ' : null}
                </span>
              ))}
            </div>
            <div className="flex flex-col items-end space-y-[6px] pt-[2px]">
              <Price cents={line.price_cents} size={34} from={line.from} />
              <Deposit cents={line.deposit_cents} size={22} inline={false} />
            </div>
          </div>
        ) : (
          <LineRow
            key={'s' + line.product.id}
            product={line.product}
            nameSize={30}
            markerSize={22}
            priceSize={34}
            pad={10}
            isFrom={isFrom}
          />
        ),
      )}
    </div>
  )
}
